import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    APIProvider,
    Map,
    Marker,
    InfoWindow,
    useMap,
} from "@vis.gl/react-google-maps";
import { getTextColorForBackground } from "@/utils/color/colorContrast";
import { setMapVisibility } from "@/features/map/mapSlice";
import Skeleton from "react-loading-skeleton";
import type { Facility } from "@/types";

const DEFAULT_CENTER = { lat: 37.0902, lng: -95.7129 };
const DEFAULT_ZOOM = 6;
const MAX_FIT_ZOOM = 14;
const BOUNDS_PADDING = { top: 60, right: 60, bottom: 60, left: 60 };

interface UnitMarketMapProps {
    facilities: Facility[];
    height: string;
    width: string;
    markerClustering?: boolean;
    pinColor?: string;
    facilityUrls?: Record<number, string>;
    unitTypeName?: string;
}

const createInfoWindowContent = (
    facility: Facility,
    url: string,
    unitTypeName: string,
) => {
    const href = url
        ? `${url}?unit_type_name=${encodeURIComponent(unitTypeName)}`
        : "";
    const button = href
        ? `<a class="unit-market-infowindow__button" href="${href}">Available Units</a>`
        : "";
    return `<div class="unit-market-infowindow">
    <h4>${facility.name}</h4>
    <p class="unit-market-infowindow__distance">(${facility.distance.toFixed(2)} miles from this location)</p>
    <p class="unit-market-infowindow__address">
      ${facility.address_1}<br />
      ${facility.city}, ${facility.state} ${facility.zipcode}
    </p>
    ${button}
  </div>`;
};

const createMarkerIconUrl = (color: string, isCluster = false) => {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="${isCluster ? 46 : 22.5}" height="${isCluster ? 46 : 30}" viewBox="0 0 ${isCluster ? 46 : 22.5} ${isCluster ? 46 : 30}">
    <g id="marker" transform="translate(${isCluster ? -291.375 : -1082} ${isCluster ? -594 : -1146.333})">
      <circle id="Ellipse_129" data-name="Ellipse 129" cx="${isCluster ? 16 : 7.5}" cy="${isCluster ? 16 : 7.5}" r="${isCluster ? 16 : 7.5}" transform="translate(${isCluster ? 298.375 : 1086} ${isCluster ? 601 : 1151})" fill="${color}"/>
      ${
          isCluster
              ? `<circle id="Ellipse_138" data-name="Ellipse 138" cx="20" cy="20" r="20" transform="translate(294.375 597)" opacity="0.499" fill="${color}"/>
      <circle id="Ellipse_139" data-name="Ellipse 139" cx="23" cy="23" r="23" transform="translate(291.375 594)" opacity="0.303" fill="${color}"/>`
              : ""
      }
      ${!isCluster ? `<path id="location-dot-sharp-solid" d="M11.25,30S22.5,17.813,22.5,11.25a11.25,11.25,0,0,0-22.5,0C0,17.813,11.25,30,11.25,30Zm0-22.5A3.75,3.75,0,1,1,7.5,11.25,3.75,3.75,0,0,1,11.25,7.5Z" transform="translate(1082 1146.333)" fill="${color}"/>` : ""}
    </g>
  </svg>
`)}`;
};

const MapContent = ({
    filteredFacilities,
    pinColor,
    facilityUrls = {},
    unitTypeName = "",
}: {
    filteredFacilities: Facility[];
    pinColor: string;
    facilityUrls?: Record<number, string>;
    unitTypeName?: string;
}) => {
    const map = useMap();
    const [selectedMarker, setSelectedMarker] = useState<number | null>(null);

    const validFacilities = filteredFacilities.filter(
        (f) => f.latitude != null && f.longitude != null,
    );

    // Auto-fit bounds when facilities change
    useEffect(() => {
        if (!map || !validFacilities.length) return;

        if (validFacilities.length === 1) {
            const facility = validFacilities[0]!;
            map.setCenter({
                lat: Number(facility.latitude),
                lng: Number(facility.longitude),
            });
            map.setZoom(12);
        } else {
            const bounds = new google.maps.LatLngBounds();
            validFacilities.forEach((facility) => {
                bounds.extend(
                    new google.maps.LatLng(
                        Number(facility.latitude),
                        Number(facility.longitude),
                    ),
                );
            });
            map.fitBounds(bounds, BOUNDS_PADDING as google.maps.Padding);

            // Limit max zoom
            const listener = google.maps.event.addListenerOnce(
                map,
                "bounds_changed",
                () => {
                    const zoom = map.getZoom();
                    if (zoom && zoom > MAX_FIT_ZOOM) {
                        map.setZoom(MAX_FIT_ZOOM);
                    }
                },
            );
            return () => google.maps.event.removeListener(listener);
        }
    }, [map, validFacilities]);

    return (
        <>
            {validFacilities.map((facility, index) => (
                <Marker
                    key={`${facility.id}-${index}`}
                    position={{
                        lat: Number(facility.latitude),
                        lng: Number(facility.longitude),
                    }}
                    onClick={() => setSelectedMarker(index)}
                    label={{
                        text: (index + 1).toString(),
                        color: getTextColorForBackground(pinColor),
                    }}
                    icon={{
                        url: createMarkerIconUrl(pinColor, false),
                    }}
                />
            ))}

            {selectedMarker !== null && validFacilities[selectedMarker] && (
                <InfoWindow
                    position={{
                        lat: Number(validFacilities[selectedMarker].latitude),
                        lng: Number(validFacilities[selectedMarker].longitude),
                    }}
                    onCloseClick={() => setSelectedMarker(null)}
                    pixelOffset={[0, -30]}
                    minWidth={300}
                    maxWidth={300}
                    style={{}}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: createInfoWindowContent(
                                validFacilities[selectedMarker],
                                facilityUrls[
                                    validFacilities[selectedMarker].id
                                ] ?? "",
                                unitTypeName,
                            ),
                        }}
                    />
                </InfoWindow>
            )}
        </>
    );
};

export const FacilityMap = ({
    facilities: filteredFacilities = [],
    height,
    width,
    pinColor = "#3033e6",
    facilityUrls = {},
    unitTypeName = "",
}: UnitMarketMapProps) => {
    const dispatch = useDispatch();
    const [googleMapsReady, setGoogleMapsReady] = useState(false);

    // Check if Google Maps is already loaded (from WordPress)
    useEffect(() => {
        const checkGoogleMaps = () => {
            if (
                typeof window.google !== "undefined" &&
                window.google?.maps?.Map
            ) {
                setGoogleMapsReady(true);
                return true;
            }
            return false;
        };

        // Check immediately
        if (checkGoogleMaps()) {
            return;
        }

        // If not loaded, wait for it to load
        const checkInterval = setInterval(() => {
            if (checkGoogleMaps()) {
                clearInterval(checkInterval);
            }
        }, 100);

        // Timeout after 10 seconds
        const timeout = setTimeout(() => {
            clearInterval(checkInterval);
            console.error("Google Maps failed to load");
        }, 10000);

        return () => {
            clearInterval(checkInterval);
            clearTimeout(timeout);
        };
    }, []);

    useEffect(() => {
        const checkMapVisibility = () => {
            dispatch(setMapVisibility(window.innerWidth > 768));
        };
        checkMapVisibility();
        window.addEventListener("resize", checkMapVisibility);
        return () => window.removeEventListener("resize", checkMapVisibility);
    }, [dispatch]);

    if (!googleMapsReady) {
        return <Skeleton height={height} width={width} />;
    }

    if (!filteredFacilities.length) {
        return <Skeleton height={height} width={width} />;
    }

    return (
        <APIProvider apiKey="">
            <Map
                style={{ height, width }}
                defaultCenter={DEFAULT_CENTER}
                defaultZoom={DEFAULT_ZOOM}
                gestureHandling="greedy"
                disableDefaultUI={false}
                mapTypeControl={false}
            >
                <MapContent
                    filteredFacilities={filteredFacilities}
                    pinColor={pinColor}
                    facilityUrls={facilityUrls}
                    unitTypeName={unitTypeName}
                />
            </Map>
        </APIProvider>
    );
};
